<template>
  <div>
    <Breadcrumbs main="Court" title="Matters" />
    <div class="container-fluid">
      <div class="row">
        <div class="col-12">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">Matters</h5>
                <span class="text-muted">Manage legal cases and court matters</span>
              </div>
              <div class="d-flex gap-2">
                <button
                  class="btn btn-outline-primary btn-sm"
                  type="button"
                  :disabled="loading"
                  @click="loadMatters"
                >
                  <vue-feather type="refresh-cw" class="me-1" />
                  Refresh
                </button>
                <button
                  v-if="canAdd"
                  class="btn btn-primary"
                  type="button"
                  @click="openCreate"
                >
                  <vue-feather type="plus" class="me-1" />
                  Add Matter
                </button>
              </div>
            </div>
            <div class="card-body">
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>
              <div v-if="loading" class="text-center py-4 text-muted">
                Loading matters...
              </div>
              <div v-else-if="matters.length === 0" class="text-center py-4 text-muted">
                No matters found.
              </div>
              <div v-else class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Case No.</th>
                      <th>Title</th>
                      <th>Client</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Lawyer</th>
                      <th>Court</th>
                      <th>Opened</th>
                      <th class="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="matter in matters" :key="matter.matterId">
                      <td>{{ matter.matterId }}</td>
                      <td>{{ matter.caseNo || '—' }}</td>
                      <td><strong>{{ matter.caseTitle || '—' }}</strong></td>
                      <td>{{ clientName(matter.clientId) }}</td>
                      <td>{{ matter.caseType || '—' }}</td>
                      <td>
                        <span class="badge" :class="statusBadgeClass(matter.status)">
                          {{ matter.status || '—' }}
                        </span>
                      </td>
                      <td>{{ matter.assignedLawyer || '—' }}</td>
                      <td>{{ matter.courtName || '—' }}</td>
                      <td>{{ formatDate(matter.openedDate) }}</td>
                      <td class="text-end">
                        <button
                          v-if="canEdit"
                          class="btn btn-sm btn-outline-primary me-1"
                          type="button"
                          title="Edit"
                          @click="openEdit(matter)"
                        >
                          <vue-feather type="edit-2" />
                        </button>
                        <button
                          v-if="canDelete"
                          class="btn btn-sm btn-outline-danger"
                          type="button"
                          title="Delete"
                          @click="confirmDelete(matter)"
                        >
                          <vue-feather type="trash-2" />
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <teleport to="body">
      <div
        v-if="showFormModal"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
          <div class="modal-content">
            <form @submit.prevent="submitForm">
              <div class="modal-header">
                <h5 class="modal-title">{{ isEditing ? 'Edit Matter' : 'Add Matter' }}</h5>
                <button type="button" class="btn-close" aria-label="Close" @click="closeFormModal" />
              </div>
              <div class="modal-body">
                <div v-if="formError" class="alert alert-danger" role="alert">
                  {{ formError }}
                </div>

                <h6 class="text-muted mb-3">Basic information</h6>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Client <span class="text-danger">*</span></label>
                      <select
                        v-model.number="form.clientId"
                        class="form-select"
                        :required="!isEditing"
                      >
                        <option :value="null" disabled>Select a client</option>
                        <option
                          v-for="client in clients"
                          :key="client.clientId"
                          :value="client.clientId"
                        >
                          {{ client.fullName || `Client #${client.clientId}` }}
                        </option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Case number</label>
                      <input
                        v-model="form.caseNo"
                        class="form-control"
                        type="text"
                        maxlength="50"
                      >
                    </div>
                  </div>
                  <div class="col-md-8">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Case title</label>
                      <input
                        v-model="form.caseTitle"
                        class="form-control"
                        type="text"
                        maxlength="255"
                      >
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Status</label>
                      <select v-model="form.status" class="form-select">
                        <option v-for="s in statusOptions" :key="s" :value="s">{{ s }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Case type</label>
                      <input
                        v-model="form.caseType"
                        class="form-control"
                        type="text"
                        maxlength="100"
                        placeholder="e.g. Civil, Criminal"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Assigned lawyer</label>
                      <input
                        v-model="form.assignedLawyer"
                        class="form-control"
                        type="text"
                        maxlength="100"
                      >
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Description</label>
                      <textarea v-model="form.description" class="form-control" rows="2" />
                    </div>
                  </div>
                </div>

                <h6 class="text-muted mb-3 mt-2">Court details</h6>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Court name</label>
                      <input
                        v-model="form.courtName"
                        class="form-control"
                        type="text"
                        maxlength="255"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Judge(s)</label>
                      <input
                        v-model="form.judges"
                        class="form-control"
                        type="text"
                        maxlength="255"
                      >
                    </div>
                  </div>
                  <div class="col-12">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Parties</label>
                      <textarea v-model="form.parties" class="form-control" rows="2" />
                    </div>
                  </div>
                </div>

                <h6 class="text-muted mb-3 mt-2">Case classification</h6>
                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Case class</label>
                      <input
                        v-model="form.caseClass"
                        class="form-control"
                        type="text"
                        maxlength="100"
                      >
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Case action</label>
                      <input
                        v-model="form.caseAction"
                        class="form-control"
                        type="text"
                        maxlength="255"
                      >
                    </div>
                  </div>
                </div>

                <h6 class="text-muted mb-3 mt-2">Outcome</h6>
                <div class="row">
                  <div class="col-md-8">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Case outcome</label>
                      <textarea v-model="form.caseOutcome" class="form-control" rows="2" />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group form-field mb-2">
                      <label class="form-label">Date delivered</label>
                      <input
                        v-model="form.dateDelivered"
                        class="form-control"
                        type="date"
                      >
                    </div>
                  </div>
                </div>
              </div>
              <div class="modal-footer">
                <button type="button" class="btn btn-secondary" @click="closeFormModal">
                  Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="saving">
                  {{ saving ? 'Saving...' : (isEditing ? 'Save Changes' : 'Create Matter') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div v-if="showFormModal" class="modal-backdrop fade show" @click="closeFormModal" />
    </teleport>

    <teleport to="body">
      <div
        v-if="matterToDelete"
        class="modal fade show d-block"
        tabindex="-1"
        role="dialog"
        aria-modal="true"
      >
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">Delete Matter</h5>
              <button type="button" class="btn-close" aria-label="Close" @click="matterToDelete = null" />
            </div>
            <div class="modal-body">
              <p class="mb-2">
                Delete
                <strong>{{ matterToDelete.caseTitle || matterToDelete.caseNo || `Matter #${matterToDelete.matterId}` }}</strong>?
              </p>
              <p class="text-muted mb-0 f-12">
                The matter will be soft-deleted and hidden from the list. The record is retained for audit.
              </p>
              <div v-if="actionError" class="alert alert-danger mt-3 mb-0" role="alert">
                {{ actionError }}
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" @click="matterToDelete = null">
                Cancel
              </button>
              <button type="button" class="btn btn-danger" :disabled="actionLoading" @click="submitDelete">
                {{ actionLoading ? 'Deleting...' : 'Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="matterToDelete" class="modal-backdrop fade show" @click="matterToDelete = null" />
    </teleport>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { listClients } from '@/services/clientService'
import {
  listMatters,
  createMatter,
  updateMatter,
  deleteMatter,
} from '@/services/matterService'

const STATUS_OPTIONS = ['OPEN', 'CLOSED', 'PENDING', 'ON HOLD', 'WITHDRAWN']

function hasMatterPrivilege(user, action) {
  if (!user?.privileges?.length) return true
  const matterPriv = user.privileges.find((p) => p.entity === 'MATTER')
  if (!matterPriv) return false
  const map = {
    view: 'canView',
    add: 'canAdd',
    edit: 'canEdit',
    delete: 'canDelete',
  }
  return matterPriv[map[action]] === true
}

const emptyForm = () => ({
  clientId: null,
  caseNo: '',
  caseTitle: '',
  caseType: '',
  description: '',
  assignedLawyer: '',
  courtName: '',
  judges: '',
  parties: '',
  caseClass: '',
  caseAction: '',
  caseOutcome: '',
  dateDelivered: '',
  status: 'OPEN',
})

export default {
  name: 'CourtMatters',

  computed: {
    ...mapGetters('auth', ['currentUser']),

    canAdd() {
      return hasMatterPrivilege(this.currentUser, 'add')
    },

    canEdit() {
      return hasMatterPrivilege(this.currentUser, 'edit')
    },

    canDelete() {
      return hasMatterPrivilege(this.currentUser, 'delete')
    },

    statusOptions() {
      return STATUS_OPTIONS
    },
  },

  data() {
    return {
      matters: [],
      clients: [],
      loading: false,
      error: '',
      showFormModal: false,
      isEditing: false,
      editingId: null,
      form: emptyForm(),
      formError: '',
      saving: false,
      matterToDelete: null,
      actionError: '',
      actionLoading: false,
    }
  },

  created() {
    this.loadMatters()
    this.loadClients()
  },

  methods: {
    async loadMatters() {
      this.loading = true
      this.error = ''

      try {
        const data = await listMatters()
        this.matters = Array.isArray(data) ? data : []
      } catch (err) {
        if (err.status === 403) {
          this.error = 'You do not have permission to view matters.'
        } else {
          this.error = err.message || 'Failed to load matters'
        }
      } finally {
        this.loading = false
      }
    },

    async loadClients() {
      try {
        const data = await listClients()
        this.clients = Array.isArray(data) ? data : []
      } catch {
        this.clients = []
      }
    },

    clientName(clientId) {
      const client = this.clients.find((c) => c.clientId === clientId)
      return client?.fullName || `#${clientId}`
    },

    statusBadgeClass(status) {
      const normalized = (status || '').toUpperCase()
      if (normalized === 'OPEN') return 'badge-success'
      if (normalized === 'CLOSED') return 'badge-secondary'
      if (normalized === 'PENDING' || normalized === 'ON HOLD') return 'badge-warning'
      return 'badge-light-primary'
    },

    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleDateString()
    },

    openCreate() {
      this.isEditing = false
      this.editingId = null
      this.form = emptyForm()
      this.formError = ''
      this.showFormModal = true
    },

    openEdit(matter) {
      this.isEditing = true
      this.editingId = matter.matterId
      this.form = {
        clientId: matter.clientId,
        caseNo: matter.caseNo || '',
        caseTitle: matter.caseTitle || '',
        caseType: matter.caseType || '',
        description: matter.description || '',
        assignedLawyer: matter.assignedLawyer || '',
        courtName: matter.courtName || '',
        judges: matter.judges || '',
        parties: matter.parties || '',
        caseClass: matter.caseClass || '',
        caseAction: matter.caseAction || '',
        caseOutcome: matter.caseOutcome || '',
        dateDelivered: matter.dateDelivered || '',
        status: matter.status || 'OPEN',
      }
      this.formError = ''
      this.showFormModal = true
    },

    closeFormModal() {
      if (this.saving) return
      this.showFormModal = false
      this.formError = ''
    },

    buildPayload() {
      const payload = {}

      const assignString = (key, value, maxLen) => {
        const trimmed = value?.trim()
        if (trimmed) payload[key] = maxLen ? trimmed.slice(0, maxLen) : trimmed
      }

      if (this.form.clientId) payload.clientId = this.form.clientId

      assignString('caseNo', this.form.caseNo, 50)
      assignString('caseTitle', this.form.caseTitle, 255)
      assignString('caseType', this.form.caseType, 100)
      assignString('description', this.form.description)
      assignString('assignedLawyer', this.form.assignedLawyer, 100)
      assignString('courtName', this.form.courtName, 255)
      assignString('judges', this.form.judges, 255)
      assignString('parties', this.form.parties)
      assignString('caseClass', this.form.caseClass, 100)
      assignString('caseAction', this.form.caseAction, 255)
      assignString('caseOutcome', this.form.caseOutcome)

      if (this.form.dateDelivered) {
        payload.dateDelivered = this.form.dateDelivered
      }

      if (this.form.status) {
        payload.status = this.form.status
      }

      return payload
    },

    async submitForm() {
      this.formError = ''

      if (!this.isEditing && !this.form.clientId) {
        this.formError = 'Please select a client.'
        return
      }

      this.saving = true

      try {
        if (this.isEditing) {
          await updateMatter({
            matterId: this.editingId,
            ...this.buildPayload(),
          })
        } else {
          await createMatter(this.buildPayload())
        }
        this.showFormModal = false
        await this.loadMatters()
      } catch (err) {
        this.formError = err.message || 'Request failed'
      } finally {
        this.saving = false
      }
    },

    confirmDelete(matter) {
      this.matterToDelete = matter
      this.actionError = ''
    },

    async submitDelete() {
      this.actionError = ''
      this.actionLoading = true

      try {
        await deleteMatter(this.matterToDelete.matterId)
        this.matterToDelete = null
        await this.loadMatters()
      } catch (err) {
        this.actionError = err.message || 'Failed to delete matter'
      } finally {
        this.actionLoading = false
      }
    },
  },
}
</script>
